import {CompositionService} from "../librettoEditor/services/CompositionService";
import { SceneThumbnailService } from "../librettoEditor/services/SceneThumbnailService";
import {EngineInternalEventsEnum} from "./EngineInternalEvents";
import {ScriptLayerStore, SceneInfo} from "../librettoEditor/store/ScriptLayerStore";
import {ScriptLayerSplit} from "./clips/script/script.interfaces";

const DEBUG_ENGINE_LOGS =
    typeof window !== "undefined" && (window as any).__LIBRETTO_DEBUG_ENGINE_LOGS === true;

class EngineEventHandlers {

    private initialized = false;
    private lastScriptLayerUpdatedAtMs = 0;
    private lastScriptLayerUpdateWasTimingOnly = false;
    private lastSceneBoundarySplitsKey: string | null = null;
    private lastScriptVideoSourcesKey: string | null = null;

    private isVisualClipType(type: unknown): boolean {
        const t = String(type ?? "").toLowerCase();
        return (
            t === "video" ||
            t === "image" ||
            t === "text" ||
            t === "captions" ||
            t === "subtitles" ||
            t === "placeholder" ||
            t === "script"
        );
    }

    private computeSceneBoundarySplitsKey(splits: ScriptLayerSplit[]): string {
        try {
            const entries = (splits ?? [])
                .filter((s) => s?.isSceneBoundary === true)
                .map((s) => ({
                    id: String((s as any)?.id ?? ""),
                    timeSec: Number((s as any)?.timeSec ?? 0),
                    label: (s as any)?.label ?? null,
                    transition: (s as any)?.transition ?? null,
                }))
                .sort((a, b) => (a.timeSec - b.timeSec) || a.id.localeCompare(b.id));
            return JSON.stringify(entries);
        } catch {
            return "";
        }
    }

    private computeScriptVideoSourcesKey(scriptClip: any): string {
        try {
            const entries: any[] =
                typeof scriptClip?.getVideoEntries === "function"
                    ? scriptClip.getVideoEntries()
                    : typeof scriptClip?.getVideoSources === "function"
                        ? scriptClip.getVideoSources()
                        : [];
            if (!Array.isArray(entries) || entries.length === 0) return "[]";

            const round = (n: any) => {
                const v = Number(n);
                if (!Number.isFinite(v)) return null;
                return Math.round(v * 1000) / 1000;
            };

            const simplified = entries.map((e) => ({
                assetId: (e as any)?.assetId ?? null,
                name: (e as any)?.name ?? null,
                layerIndex: Number((e as any)?.layerIndex ?? 0),
                visual: (e as any)?.visual
                    ? {
                        x: round((e as any).visual.x),
                        y: round((e as any).visual.y),
                        width: round((e as any).visual.width),
                        height: round((e as any).visual.height),
                        alpha: round((e as any).visual.alpha),
                        rotation: round((e as any).visual.rotation),
                        maskShape: (e as any).visual.maskShape ?? null,
                        crop: (e as any).visual.crop
                            ? {
                                x: round((e as any).visual.crop.x),
                                y: round((e as any).visual.crop.y),
                                width: round((e as any).visual.crop.width),
                                height: round((e as any).visual.crop.height),
                            }
                            : null,
                    }
                    : null,
            }));

            return JSON.stringify(simplified);
        } catch {
            return "";
        }
    }

    init() {
        if (this.initialized) return;
        this.setupEventListeners();
    }

    teardown() {
        if (!this.initialized) return;
        try {
            const composition = CompositionService.composition as any;
            composition.off?.(EngineInternalEventsEnum.ASSET_SOURCE_REPLACED, this.handleAssetSourceReplaced);
            composition.off?.(EngineInternalEventsEnum.CLIP_SOURCE_REPLACED, this.handleClipSourceReplaced);
            composition.off?.(EngineInternalEventsEnum.CLIP_REMOVED, this.handleClipRemoved);
            composition.off?.(EngineInternalEventsEnum.CLIP_UPDATED, this.handleClipUpdated);
            composition.off?.(EngineInternalEventsEnum.CLIP_ADDED, this.handleClipAdded);
            composition.off?.(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, this.handleScriptLayerUpdated);
        } catch {}
        this.initialized = false;
    }

    private setupEventListeners() {
        const composition = CompositionService.composition;
        composition.on(EngineInternalEventsEnum.ASSET_SOURCE_REPLACED, this.handleAssetSourceReplaced)
        composition.on(EngineInternalEventsEnum.CLIP_SOURCE_REPLACED, this.handleClipSourceReplaced)
        composition.on(EngineInternalEventsEnum.CLIP_REMOVED, this.handleClipRemoved)
        composition.on(EngineInternalEventsEnum.CLIP_UPDATED, this.handleClipUpdated)
        composition.on(EngineInternalEventsEnum.CLIP_ADDED, this.handleClipAdded)
        composition.on(EngineInternalEventsEnum.SCRIPT_LAYER_UPDATED, this.handleScriptLayerUpdated)
        this.initialized = true;
    }

    private handleAssetSourceReplaced() {
        // Todo: handle asset source replaced
    }

    private handleClipSourceReplaced() {
        // Todo: handle clip source replaced
    }

    /**
     * Handle clip added - regenerate scene thumbnails since visual content may have changed.
     */
    public handleClipAdded = async (event) => {
        if (DEBUG_ENGINE_LOGS) {
            console.log("[EngineEventHandlers] CLIP_ADDED event received", {
                hasVideo: ScriptLayerStore.hasVideo,
                scenesCount: ScriptLayerStore.scenes.length,
            });
        }
        
        const clipType = (event as any)?.detail?.clip?.type;
        if (clipType && !this.isVisualClipType(clipType)) return;

        // Regenerate thumbnails if we have scenes (video or not - thumbnails show what's on canvas)
        if (ScriptLayerStore.scenes.length === 0) {
            if (DEBUG_ENGINE_LOGS) {
                console.log("[EngineEventHandlers] No scenes yet, skipping thumbnail regeneration");
            }
            return;
        }
        if (DEBUG_ENGINE_LOGS) {
            console.log("[EngineEventHandlers] Clip added, regenerating scene thumbnails");
        }
        SceneThumbnailService.requestRefresh();
    }

    /**
     * Handle clip removed - delete empty layers and regenerate thumbnails.
     */
    private handleClipRemoved = async (event) => {
        if (DEBUG_ENGINE_LOGS) {
            console.log("[EngineEventHandlers] CLIP_REMOVED event received", {
                hasVideo: ScriptLayerStore.hasVideo,
                scenesCount: ScriptLayerStore.scenes.length,
            });
        }
        
        const layerId = event.detail.layerId;
        const track = CompositionService.composition.tracks.find(track => track.id === layerId);
        if (!track) return;
        if (track.clips.length === 0) {
            CompositionService.deleteLayer(layerId)
        }
        
        // Regenerate thumbnails if we have scenes
        if (ScriptLayerStore.scenes.length > 0) {
            if (DEBUG_ENGINE_LOGS) {
                console.log("[EngineEventHandlers] Clip removed, regenerating scene thumbnails");
            }
            SceneThumbnailService.requestRefresh();
        }
    }

    /**
     * Handle clip updated - regenerate scene thumbnails since visual content may have changed.
     */
    private handleClipUpdated = async (event) => {
        if (DEBUG_ENGINE_LOGS) {
            console.log("[EngineEventHandlers] CLIP_UPDATED event received", {
                hasVideo: ScriptLayerStore.hasVideo,
                scenesCount: ScriptLayerStore.scenes.length,
            });
        }
        
        const clip = (event as any)?.detail?.clip;
        const clipType = clip?.type;
        if (clipType && !this.isVisualClipType(clipType)) return;

        // Many transcript operations (cuts/gaps) shift clip timings in bulk; those changes should
        // not force a full scene thumbnail regeneration since the visuals at scene-local times are
        // typically unchanged. SCRIPT_LAYER_UPDATED decides whether thumbnails need refreshing.
        {
            const dt = Date.now() - this.lastScriptLayerUpdatedAtMs;
            if (dt >= 0 && dt < 500 && this.lastScriptLayerUpdateWasTimingOnly) return;
        }

        // Regenerate thumbnails if we have scenes
        if (ScriptLayerStore.scenes.length === 0) {
            if (DEBUG_ENGINE_LOGS) {
                console.log("[EngineEventHandlers] No scenes yet, skipping thumbnail regeneration");
            }
            return;
        }
        if (DEBUG_ENGINE_LOGS) {
            console.log("[EngineEventHandlers] Clip updated, regenerating scene thumbnails");
        }
        SceneThumbnailService.requestRefresh();
    }
    

    private handleScriptLayerUpdated = async (event) => {
        if (DEBUG_ENGINE_LOGS) {
            console.log("HandleScriptLayerUpdated called");
        }

        const { cuts, gaps, segments, splits, duration, hasVideo } = event.detail ?? {};
        
        if (DEBUG_ENGINE_LOGS) {
            console.log("[EngineEventHandlers] Script layer updated:", {
                cutsCount: cuts?.length ?? 0,
                gapsCount: gaps?.length ?? 0,
                segmentsCount: segments?.length ?? 0,
                splitsCount: splits?.length ?? 0,
                duration,
                hasVideo,
            });
        }
        
        // Update cuts, gaps, segments
        ScriptLayerStore.updateCutsAndSegments(cuts ?? [], segments ?? [], gaps ?? []);

        // Always try to derive scenes - get data from event or clip
        const clip = CompositionService.getScriptLayerClip();

        // Sync playbackRate for transcript ↔ timeline time translation
        const clipPlaybackRate = (clip as any)?.playbackRate ?? 1;
        ScriptLayerStore.setPlaybackRate(clipPlaybackRate);
        const clipSplits: ScriptLayerSplit[] = splits ?? clip?.splits ?? [];

        // Duration can be missing or sometimes reported as 0 during early init. Fall back to the
        // engine clip's own duration/range so empty spines still derive scenes immediately.
        const durationFromEvent = Number(duration);
        const durationFromClip = Number(clip?.duration?.seconds ?? 0);
        const inSec = Number(clip?.range?.[0]?.seconds ?? 0);
        const outSec = Number(clip?.range?.[1]?.seconds ?? durationFromClip);
        const durationFromRange = Number.isFinite(inSec) && Number.isFinite(outSec) ? Math.max(0, outSec - inSec) : durationFromClip;
        const clipDuration =
            Number.isFinite(durationFromEvent) && durationFromEvent > 0
                ? durationFromEvent
                : Number.isFinite(durationFromRange) && durationFromRange > 0
                    ? durationFromRange
                    : 0;
        const clipHasVideo = hasVideo ?? (clip?.getVideoSources?.()?.length ?? 0) > 0;
        
        if (DEBUG_ENGINE_LOGS) {
            console.log("[EngineEventHandlers] Deriving scenes:", {
                splitsCount: clipSplits.length,
                duration: clipDuration,
                hasVideo: clipHasVideo,
            });
        }
        
        // Only derive scenes if we have a valid duration
        if (clipDuration > 0) {
            const prevHasVideo = ScriptLayerStore.hasVideo;
            const prevScenes = Array.isArray(ScriptLayerStore.scenes) ? [...ScriptLayerStore.scenes] : [];

            // Derive scenes from splits
            const scenes = this.deriveScenesFromSplits(clipSplits, clipDuration);
            const splitsKey = this.computeSceneBoundarySplitsKey(clipSplits);
            const videoSourcesKey = this.computeScriptVideoSourcesKey(clip);
            const splitsMetaChanged = this.lastSceneBoundarySplitsKey !== null && splitsKey !== this.lastSceneBoundarySplitsKey;
            const videoSourcesChanged = this.lastScriptVideoSourcesKey !== null && videoSourcesKey !== this.lastScriptVideoSourcesKey;
            
            if (DEBUG_ENGINE_LOGS) {
                console.log("[EngineEventHandlers] Derived scenes:", scenes.length);
            }
            
            // Update store with scenes metadata (no thumbnails yet)
            ScriptLayerStore.updateScenes(scenes, clipDuration, clipHasVideo);
            this.lastScriptLayerUpdatedAtMs = Date.now();
            this.lastSceneBoundarySplitsKey = splitsKey;
            this.lastScriptVideoSourcesKey = videoSourcesKey;
            
            // Generate thumbnails using a detached composition (never touches the live preview engine).
            // Avoid regenerating on transcript-only edits when the scene boundaries + visuals are unchanged.
            const EPS = 1e-3;
            const boundariesChanged =
                prevScenes.length !== scenes.length ||
                scenes.some((s, idx) => {
                    const prev = prevScenes[idx];
                    if (!prev) return true;
                    if (String(prev.id) !== String(s.id)) return true;
                    return (
                        Math.abs(Number(prev.startSec ?? 0) - Number(s.startSec ?? 0)) > EPS ||
                        Math.abs(Number(prev.endSec ?? 0) - Number(s.endSec ?? 0)) > EPS
                    );
                });
            const hasMissingThumbs = ScriptLayerStore.scenes.some((s) => !s?.thumbnailUrl);

            this.lastScriptLayerUpdateWasTimingOnly = !(
                boundariesChanged ||
                prevHasVideo !== clipHasVideo ||
                splitsMetaChanged ||
                videoSourcesChanged ||
                hasMissingThumbs
            );

            if (
                scenes.length > 0 &&
                (boundariesChanged || prevHasVideo !== clipHasVideo || splitsMetaChanged || videoSourcesChanged || hasMissingThumbs)
            ) {
                SceneThumbnailService.requestRefresh();
            }
        }
    }
    
    /**
     * Derive scene boundaries from splits.
     * Splits are boundary points; scenes are the regions between them.
     */
    private deriveScenesFromSplits(splits: ScriptLayerSplit[], duration: number): SceneInfo[] {
        const scenes: SceneInfo[] = [];
        
        // Only scene-boundary splits create scenes on the scene bar.
        // Non-scene splits are "section markers" used for timeline selection/ops but should not
        // create scenes.
        const sortedSplits = [...splits]
            .filter((s) => s?.isSceneBoundary === true)
            .sort((a, b) => a.timeSec - b.timeSec);
        
        // Start with scene 0 from 0 to first split (or full duration if no splits)
        let prevTime = 0;
        
        for (let i = 0; i < sortedSplits.length; i++) {
            const split = sortedSplits[i];
            scenes.push({
                id: `scene_${i}`,
                index: i,
                startSec: prevTime,
                endSec: split.timeSec,
                thumbnailUrl: null,
            });
            prevTime = split.timeSec;
        }
        
        // Final scene from last split to end
        scenes.push({
            id: `scene_${sortedSplits.length}`,
            index: sortedSplits.length,
            startSec: prevTime,
            endSec: duration,
            thumbnailUrl: null,
        });
        
        return scenes;
    }
    
}

export default new EngineEventHandlers();
